<template>
  <div class="job-filter">
    <div
      v-for="filter in filters"
      :id="filter.id"
      :key="filter.id"
      class="filter-item"
      :class="{
        '-open': openFilter === filter.id,
        '-selected': hasFilterSelection(filter.dataType)
      }"
      @click="toggleFilter(filter.id)"
    >
      <div class="label">
        <span class="label-text text-sm">
          {{ getLabelText(filter) }}
        </span>
        <button
          v-if="hasFilterSelection(filter.dataType)"
          class="icon -clear"
          type="button"
          @click.stop="clearFilter(filter.dataType)"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#filterItemClearIcon)">
              <path
                d="M0.25 0.249512L11.75 11.7495"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M11.75 0.249512L0.25 11.7495"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="filterItemClearIcon">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <div v-else class="icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
      <div class="options">
        <div class="options-list">
          <label
            v-for="value in filter.options"
            :key="value"
            class="option"
            :class="{ '-selected': isOptionSelected(filter.dataType, value) }"
            :data-type="filter.dataType"
            @click.stop="selectOption(filter, value)"
          >
            <span class="label text-sm">
              {{ filter.formatOption ? filter.formatOption(value) : value }}
            </span>
          </label>
        </div>
      </div>
    </div>
    <button
      class="clear-all text-sm"
      :class="{ '-visible': hasSelected }"
      type="button"
      @click="clearAllFilters"
    >
      Filter zurücksetzen
    </button>
  </div>
</template>

<script setup lang="ts">
import type { JobPosition } from '@/types/jobsList.types';

interface FilterConfig {
  id: string;
  dataType: string;
  label: string;
  instructions: string;
  options: string[];
  formatOption?: (value: string) => string;
}

import { formatEmploymentTypeOption } from '~/utils/jobs/employmentTypeFilter';
import { formatScheduleOption } from '~/utils/jobs/scheduleFilter';

const props = defineProps<{
  positions: JobPosition[];
  preselectedEmploymentTypes?: string[];
}>();

const emit = defineEmits<{
  'filter-change': [activeFilters: Record<string, string[]>];
}>();

const openFilter = ref<string | null>(null);
let outsideClickListener: ((event: MouseEvent) => void) | null = null;
let escKeyListener: ((event: KeyboardEvent) => void) | null = null;

function removeOutsideClickListener() {
  if (!outsideClickListener || typeof document === 'undefined') {
    return;
  }
  document.removeEventListener('click', outsideClickListener);
  outsideClickListener = null;
}

function removeEscKeyListener() {
  if (!escKeyListener || typeof document === 'undefined') {
    return;
  }
  document.removeEventListener('keydown', escKeyListener);
  escKeyListener = null;
}

function removeCloseListeners() {
  removeOutsideClickListener();
  removeEscKeyListener();
}

function closeOpenFilters() {
  openFilter.value = null;
  removeCloseListeners();
}

function addOutsideClickListener() {
  if (outsideClickListener || typeof document === 'undefined') {
    return;
  }

  outsideClickListener = (event: MouseEvent) => {
    const target = event.target as Node | null;
    const clickedInsideFilter =
      target instanceof Element && target.closest('.job-filter .filter-item');
    if (clickedInsideFilter) {
      return;
    }

    closeOpenFilters();
  };

  document.addEventListener('click', outsideClickListener);
}

function addEscKeyListener() {
  if (escKeyListener || typeof document === 'undefined') {
    return;
  }

  escKeyListener = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      return;
    }
    closeOpenFilters();
  };

  document.addEventListener('keydown', escKeyListener);
}

const activeFilters = ref<Record<string, string[]>>({
  employmentType: [...(props.preselectedEmploymentTypes ?? [])],
  schedule: [],
  location: [],
  department: []
});

watch(
  () => props.preselectedEmploymentTypes,
  (value) => {
    if (value?.length) {
      activeFilters.value.employmentType = [...value];
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (props.preselectedEmploymentTypes?.length) {
    emit('filter-change', { ...activeFilters.value });
  }
});

const filters = computed<FilterConfig[]>(() => [
  {
    id: 'employmentType',
    dataType: 'employmentType',
    label: 'Anstellungsart',
    instructions: 'Wähle eine oder mehrere',
    options: [...new Set(props.positions.map((p) => p.employmentType))],
    formatOption: formatEmploymentTypeOption
  },
  {
    id: 'schedule',
    dataType: 'schedule',
    label: 'Pensum',
    instructions: 'Wähle eine oder mehrere',
    options: [
      ...new Set(
        props.positions.filter((p) => p.schedule).map((p) => p.schedule)
      )
    ],
    formatOption: formatScheduleOption
  },
  {
    id: 'location',
    dataType: 'location',
    label: 'Ort',
    instructions: 'Wähle eine oder mehrere',
    options: [
      ...new Set(props.positions.map((p) => p.locationLabel ?? p.office))
    ]
  },
  {
    id: 'department',
    dataType: 'department',
    label: 'Abteilung',
    instructions: 'Wähle eine oder mehrere',
    options: [
      ...new Set(
        props.positions
          .filter((p) => p.departmentFilterLabel)
          .map((p) => p.departmentFilterLabel!)
      )
    ]
  }
]);

const hasSelected = computed(() =>
  Object.values(activeFilters.value).some((arr) => arr.length > 0)
);

function getLabelText(filter: FilterConfig): string {
  const selected = activeFilters.value[filter.dataType] ?? [];
  if (selected.length === 0) {
    return filter.label;
  }
  if (filter.formatOption) {
    return selected.map(filter.formatOption).join(', ');
  }
  return selected.join(', ');
}

function isOptionSelected(dataType: string, value: string): boolean {
  return (activeFilters.value[dataType] ?? []).includes(value);
}

function toggleFilter(id: string) {
  const isOpening = openFilter.value !== id;
  openFilter.value = isOpening ? id : null;

  if (isOpening) {
    addOutsideClickListener();
    addEscKeyListener();
  } else {
    removeCloseListeners();
  }
}

onUnmounted(() => {
  removeCloseListeners();
});

function hasFilterSelection(dataType: string): boolean {
  return (activeFilters.value[dataType] ?? []).length > 0;
}

function selectOption(filter: FilterConfig, value: string) {
  const current = activeFilters.value[filter.dataType] ?? [];
  const idx = current.indexOf(value);
  if (idx === -1) {
    activeFilters.value[filter.dataType] = [...current, value];
  } else {
    activeFilters.value[filter.dataType] = current.filter((v) => v !== value);
  }
  emit('filter-change', { ...activeFilters.value });
}

function clearFilter(dataType: string) {
  activeFilters.value[dataType] = [];
  openFilter.value = null;
  emit('filter-change', { ...activeFilters.value });
}

function clearAllFilters() {
  openFilter.value = null;
  activeFilters.value = {
    employmentType: [],
    schedule: [],
    location: [],
    department: []
  };
  emit('filter-change', { ...activeFilters.value });
}
</script>

<style scoped lang="scss">
.job-filter {
  display: grid;
  grid-column: 1 / span 4;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 1rem;
  margin-bottom: 2.5rem;

  @media (--vs) {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0;
  }

  > .clear-all {
    @include text-sm(300);
    display: none;
    align-self: center;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    padding: 1rem;
    color: var(--base-color-foreground-faded);
    text-align: left;
    width: fit-content;

    &.-visible {
      display: block;
    }

    @media (--vs) {
      padding-left: 0;
    }
  }

  > .filter-item {
    @include text-sm(300);
    cursor: pointer;
    border: 1px solid hsl(0, 0%, 90%, 1);
    padding: 1rem;
    position: relative;
    min-width: 16rem;

    @media (--vs) {
    }

    &:hover {
      background-color: var(--color-white);
    }

    > .label {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      overflow: hidden;
      cursor: pointer;

      > .label-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--base-color-foreground-faded);
      }

      > .icon {
        flex-shrink: 0;
        transition: transform 0.3s ease-in-out;

        &.-clear {
          border: none;
          background: none;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
      }
    }

    > .options {
      display: none;
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      background-color: var(--color-off-white);
      border: 2px solid var(--base-color-lines);
      z-index: 10;
      box-shadow: 2px 2px 10px 0 rgba(170, 170, 204, 0.25);

      @media (--vs) {
        width: 100%;
        max-height: 50vh;
      }

      > .options-util {
        display: flex;
        justify-content: space-between;
        margin-bottom: 1.5rem;
        gap: 1rem;
        align-items: center;
        color: var(--base-color-foreground-faded);

        > .close {
          padding-right: 0.5rem;
        }
      }

      > .options-list {
        display: flex;
        flex-direction: column;
        max-height: 40vh;
        overflow-y: auto;

        @media (--vs) {
          max-height: 100%;
        }

        > .option {
          padding: 1rem;
          border-bottom: 1px solid hsl(0, 0%, 90%, 1);
          cursor: pointer;

          &:first-child {
            border-top: 1px solid hsl(0, 0%, 90%, 1);
          }

          @media (hover: hover) {
            &:hover {
              background-color: var(--color-white);
            }
          }

          &.-selected {
            @include text-sm(600);
            background-color: var(--color-white);

            > .label {
              font-weight: 600;
            }
          }

          @media (--vs) {
            padding: 1.125rem;
          }
        }
      }

      > .confirm {
        width: 100%;
        display: flex;
        justify-content: center;
        margin-top: 1rem;

        @media (--vl) {
          display: none;
        }

        > .button {
          width: 100%;
          background-color: var(--base-color-brand);
          border: none;
          color: var(--base-color-background);
          padding-block: 0.5rem;
        }
      }
    }

    &.-open {
      > .label > .icon:not(.-clear) {
        transform: rotate(180deg);
      }

      > .options {
        display: block;

        @media (--vs) {
          display: flex;
          flex-direction: column;
        }
      }
    }

    &.-selected {
      > .label > .label-text {
        font-weight: 600;
      }
    }
  }
}
</style>
